import { useEffect, useRef, useState } from "react";
import { Undo2, Eraser } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";

export const SIG_FONTS = ["Great Vibes", "Dancing Script", "Sacramento", "Allura", "Homemade Apple", "Caveat"];
const INKS = ["#0F172A", "#1E3A8A", "#0F6CBD", "#7F1D1D"];

const Inks = ({ value, onChange, prefix }) => (
  <div className="flex items-center gap-1.5">
    {INKS.map((c) => (
      <button key={c} type="button" onClick={() => onChange(c)} data-testid={`${prefix}-ink-${c.slice(1)}`} aria-label={`Ink ${c}`}
        className={`h-6 w-6 rounded-full border-2 ${value === c ? "border-primary ring-2 ring-primary/30" : "border-transparent"}`} style={{ background: c }} />
    ))}
  </div>
);

const drawStroke = (ctx, s) => {
  ctx.strokeStyle = s.color;
  ctx.fillStyle = s.color;
  ctx.lineWidth = s.width * 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const p = s.pts;
  if (p.length === 1) { ctx.beginPath(); ctx.arc(p[0].x, p[0].y, s.width, 0, Math.PI * 2); ctx.fill(); return; }
  ctx.beginPath();
  ctx.moveTo(p[0].x, p[0].y);
  for (let i = 1; i < p.length - 1; i++) ctx.quadraticCurveTo(p[i].x, p[i].y, (p[i].x + p[i + 1].x) / 2, (p[i].y + p[i + 1].y) / 2);
  ctx.lineTo(p[p.length - 1].x, p[p.length - 1].y);
  ctx.stroke();
};

function cropToPng(strokes, w, h) {
  const pts = strokes.flatMap((s) => s.pts);
  const pad = 16;
  const x0 = Math.max(0, Math.min(...pts.map((p) => p.x)) - pad), y0 = Math.max(0, Math.min(...pts.map((p) => p.y)) - pad);
  const x1 = Math.min(w, Math.max(...pts.map((p) => p.x)) + pad), y1 = Math.min(h, Math.max(...pts.map((p) => p.y)) + pad);
  const c = document.createElement("canvas");
  c.width = x1 - x0;
  c.height = y1 - y0;
  const ctx = c.getContext("2d");
  ctx.translate(-x0, -y0);
  strokes.forEach((s) => drawStroke(ctx, s));
  return c.toDataURL("image/png");
}

export const DrawPad = ({ onChange }) => {
  const ref = useRef();
  const strokes = useRef([]);
  const cur = useRef(null);
  const [ink, setInk] = useState(INKS[0]);
  const [width, setWidth] = useState(2.5);
  const [count, setCount] = useState(0);
  const W = 1120, H = 360;

  const redraw = () => {
    const ctx = ref.current.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    strokes.current.forEach((s) => drawStroke(ctx, s));
  };
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return { x: ((e.clientX - r.left) * W) / r.width, y: ((e.clientY - r.top) * H) / r.height }; };
  const publish = () => { setCount(strokes.current.length); onChange(strokes.current.length ? cropToPng(strokes.current, W, H) : ""); };

  return (
    <div className="space-y-3" data-testid="signature-draw-pad">
      <div className="relative rounded-lg border bg-white">
        <canvas ref={ref} width={W} height={H} className="w-full h-44 touch-none cursor-crosshair rounded-lg" data-testid="signature-draw-canvas"
          onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); cur.current = { color: ink, width, pts: [pos(e)] }; strokes.current.push(cur.current); redraw(); }}
          onPointerMove={(e) => { if (!cur.current) return; cur.current.pts.push(pos(e)); redraw(); }}
          onPointerUp={() => { if (cur.current) { cur.current = null; publish(); } }}
          onPointerLeave={() => { if (cur.current) { cur.current = null; publish(); } }} />
        <div className="pointer-events-none absolute left-6 right-6 bottom-10 border-t border-slate-300" />
        <span className="pointer-events-none absolute left-6 bottom-4 text-[11px] text-slate-400">✕ Sign above the line with your mouse, pen or finger</span>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <Inks value={ink} onChange={setInk} prefix="draw" />
        <div className="flex items-center gap-2 text-xs text-muted-foreground w-40">Pen<Slider min={1} max={6} step={0.5} value={[width]} onValueChange={([v]) => setWidth(v)} data-testid="signature-pen-width" /></div>
        <div className="ml-auto flex gap-1.5">
          <Button type="button" size="sm" variant="outline" disabled={!count} onClick={() => { strokes.current.pop(); redraw(); publish(); }} data-testid="signature-draw-undo"><Undo2 className="h-4 w-4 mr-1" />Undo</Button>
          <Button type="button" size="sm" variant="outline" disabled={!count} onClick={() => { strokes.current = []; redraw(); publish(); }} data-testid="signature-draw-clear"><Eraser className="h-4 w-4 mr-1" />Clear</Button>
        </div>
      </div>
    </div>
  );
};

export async function typedToPng(text, font, color) {
  await document.fonts.load(`72px "${font}"`);
  const c = document.createElement("canvas");
  let ctx = c.getContext("2d");
  ctx.font = `72px "${font}"`;
  c.width = Math.ceil(ctx.measureText(text).width) + 48;
  c.height = 140;
  ctx = c.getContext("2d");
  ctx.font = `72px "${font}"`;
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  ctx.fillText(text, 24, 72);
  return c.toDataURL("image/png");
}

export const TypePad = ({ value, onChange }) => {
  const { text, font, color } = value;
  const set = (p) => onChange({ ...value, ...p });
  useEffect(() => { SIG_FONTS.forEach((f) => document.fonts.load(`32px "${f}"`)); }, []);
  return (
    <div className="space-y-3" data-testid="signature-type-pad">
      <Input value={text} onChange={(e) => set({ text: e.target.value })} placeholder="Type your full name" data-testid="signature-type-input" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {SIG_FONTS.map((f, i) => (
          <button key={f} type="button" onClick={() => set({ font: f })} data-testid={`signature-font-${i}`}
            className={`h-16 rounded-lg border bg-white px-3 text-left overflow-hidden transition-colors ${font === f ? "border-primary ring-2 ring-primary/20" : "hover:border-primary/50"}`}>
            <span className="block truncate text-[26px] leading-tight" style={{ fontFamily: `"${f}", cursive`, color }}>{text || "Your Name"}</span>
            <span className="block text-[10px] text-slate-400 font-sans">{f}</span>
          </button>
        ))}
      </div>
      <Inks value={color} onChange={(c) => set({ color: c })} prefix="type" />
    </div>
  );
};

export const SavedList = ({ items, onPick, onDelete }) => (
  <div className="space-y-2" data-testid="signature-saved-list">
    {!items.length && <p className="text-sm text-muted-foreground py-8 text-center">No saved signatures yet. Sign once with “Save for reuse” ticked and it will appear here.</p>}
    <div className="grid grid-cols-2 gap-2.5">
      {items.map((s, i) => (
        <div key={s.id} className="group relative rounded-lg border bg-white hover:border-primary hover:ring-2 hover:ring-primary/20 transition">
          <button type="button" onClick={() => onPick(s)} className="w-full h-24 flex flex-col items-center justify-center px-3" data-testid={`saved-signature-${i}`} title="Use this signature">
            <img src={s.src} alt={s.label || "Saved signature"} className="max-h-14 max-w-full object-contain" />
            <span className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">{s.kind === "initials" ? "Initials" : "Signature"}{s.label ? ` · ${s.label}` : ""}</span>
          </button>
          <button type="button" onClick={() => onDelete(s)} className="absolute top-1 right-1 h-6 w-6 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 text-sm" data-testid={`saved-signature-delete-${i}`} aria-label="Delete saved signature">×</button>
        </div>
      ))}
    </div>
  </div>
);
